import React, { useCallback, useMemo, useState } from "react";
import "../../css/items.scss";
import { serverInstance } from "../../service/axios";
import "./MeetingPayment.scss";
import { formatAmount, formatPricingDate, getPricingPreview, getTierLabel } from "./meetingPricing";

const MEETING_QPAY_INVOICE_CODE = "HURALBURTGEL_MSID_INVOICE";

const MeetingPayment = ({ email, resident, meetingId, meetingInfo, membershipActive, attendeeId, initialInvoiceId, couponCode, couponPercent, language, onPaid, onInvoiceChange }) => {
  const [qpayInvoice, setQpayInvoice] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [pricingInfo, setPricingInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [gettingInvoice, setGettingInvoice] = useState(false);
  const [checking, setChecking] = useState(false);
  const [refreshingInvoice, setRefreshingInvoice] = useState(false);
  const [paid, setPaid] = useState(false);
  const normalizedCouponCode = useMemo(() => String(couponCode || "").trim().toUpperCase(), [couponCode]);
  const normalizedCouponPercent = useMemo(() => {
    const percent = Number(couponPercent);
    return Number.isFinite(percent) && percent > 0 && percent < 100 ? percent : 0;
  }, [couponPercent]);
  const pricingPreview = useMemo(() => getPricingPreview({ meetingInfo, resident, membershipActive }), [meetingInfo, resident, membershipActive]);
  const couponPricingPreview = useMemo(() => {
    if (!pricingPreview || !normalizedCouponCode || !normalizedCouponPercent) {
      return pricingPreview;
    }

    const previewAmount = Number(pricingPreview.amount);
    if (!Number.isFinite(previewAmount)) {
      return pricingPreview;
    }

    const couponDiscountAmount = Math.max(0, Math.min(previewAmount, Math.round(previewAmount * normalizedCouponPercent / 100)));

    return {
      ...pricingPreview,
      amount: Math.max(0, previewAmount - couponDiscountAmount),
      discount_amount: Number(pricingPreview.discount_amount || 0) + couponDiscountAmount,
      meeting_discount_amount: Number(pricingPreview.discount_amount || 0),
      meeting_discount_code: pricingPreview.discount_code || "",
      meeting_discount_label: pricingPreview.discount_label || "",
      coupon_code: normalizedCouponCode,
      coupon_discount_amount: couponDiscountAmount,
      coupon_percent: normalizedCouponPercent,
      source: "meeting_coupon_preview",
    };
  }, [pricingPreview, normalizedCouponCode, normalizedCouponPercent]);
  const pricingSummary = pricingInfo || couponPricingPreview;
  const meetingInvoiceStorageKey = `meeting_invoice_id_${meetingId || "unknown"}_${pricingPreview?.tier || "unknown"}_${pricingPreview?.amount ?? "unknown"}_${normalizedCouponCode || "no-coupon"}`;
  const legacyMeetingInvoiceStorageKey = `meeting_invoice_id_${meetingId || "unknown"}`;
  const normalizedAttendeeId = useMemo(() => String(attendeeId || "").trim().toLowerCase(), [attendeeId]);
  const normalizedInitialInvoiceId = useMemo(() => String(initialInvoiceId || "").trim(), [initialInvoiceId]);
  const isBusy = gettingInvoice || checking || refreshingInvoice;

  const getRequestConfig = (token, params) => {
    const config = {};
    if (params) {
      config.params = params;
    }
    if (token) {
      config.headers = {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      };
    }
    return config;
  };

  const normalizeInvoicePricing = useCallback((pricing) => pricing ? {
    ...pricing,
    tier: pricing.tier || pricing.pricing_tier || pricingPreview?.tier,
    amount: pricing.amount,
    currency: pricing.currency || "MNT",
    discount_ends_at: pricing.discount_ends_at || pricing.meeting_discount_ends_at || pricingPreview?.discount_ends_at || "",
    coupon_code: String(pricing.coupon_code || "").trim().toUpperCase(),
  } : null, [pricingPreview?.discount_ends_at, pricingPreview?.tier]);

  const invoiceMatchesPreview = useCallback((invoicePricing) => {
    if (!pricingPreview || !invoicePricing) {
      return true;
    }

    if (invoicePricing.tier !== pricingPreview.tier) {
      return false;
    }
    if (String(invoicePricing.coupon_code || "").trim().toUpperCase() !== normalizedCouponCode) {
      return false;
    }

    return normalizedCouponCode
      ? true
      : Number(invoicePricing.amount) === Number(pricingPreview.amount);
  }, [normalizedCouponCode, pricingPreview]);

  const persistInvoiceId = useCallback((nextInvoiceId) => {
    const normalizedInvoiceIdValue = String(nextInvoiceId || "").trim();
    if (normalizedInvoiceIdValue) {
      localStorage.setItem(meetingInvoiceStorageKey, normalizedInvoiceIdValue);
    } else {
      localStorage.removeItem(meetingInvoiceStorageKey);
      localStorage.removeItem(legacyMeetingInvoiceStorageKey);
    }
    onInvoiceChange?.(normalizedInvoiceIdValue);
  }, [legacyMeetingInvoiceStorageKey, meetingInvoiceStorageKey, onInvoiceChange]);

  const clearInvoiceDisplay = useCallback(() => {
    setInvoiceId(null);
    setQpayInvoice(null);
    setPricingInfo(null);
    persistInvoiceId("");
  }, [persistInvoiceId]);

  const markInvoicePaid = useCallback(() => {
    setSuccessMsg(language ? "Payment completed successfully" : "Төлбөр амжилттай төлөгдсөн байна");
    setErrorMsg("");
    setPaid(true);
    clearInvoiceDisplay();
    onPaid?.();
  }, [clearInvoiceDisplay, language, onPaid]);

  const resolveInvoicePricing = useCallback((responseData) => {
    return normalizeInvoicePricing(responseData?.pricing || (responseData?.amount !== undefined ? {
      amount: responseData.amount,
      currency: "MNT",
      tier: responseData.pricing_tier || pricingPreview?.tier,
      coupon_code: normalizedCouponCode,
    } : null));
  }, [normalizeInvoicePricing, normalizedCouponCode, pricingPreview?.tier]);

  const applyInvoiceResponse = useCallback((responseData, { preserveExistingOnMismatch = false } = {}) => {
    if (responseData?.paid === true) {
      markInvoicePaid();
      return true;
    }

    const nextInvoicePricing = resolveInvoicePricing(responseData);
    if (!invoiceMatchesPreview(nextInvoicePricing)) {
      if (!preserveExistingOnMismatch) {
        clearInvoiceDisplay();
      }
      setErrorMsg(language
        ? "Invoice price does not match the selected attendee type. Please try again."
        : "Нэхэмжлэхийн үнэ сонгосон төрөлтэй тохирохгүй байна. Дахин оролдоно уу.");
      return false;
    }

    if (!responseData?.invoice_id || !responseData?.qpay_invoice) {
      setErrorMsg(language ? "Could not load the payment invoice" : "Төлбөрийн нэхэмжлэх ачаалж чадсангүй!");
      return false;
    }

    setInvoiceId(responseData.invoice_id);
    setQpayInvoice(responseData.qpay_invoice);
    setPricingInfo(nextInvoicePricing);
    setErrorMsg("");
    setSuccessMsg("");
    persistInvoiceId(responseData.invoice_id);
    return true;
  }, [clearInvoiceDisplay, invoiceMatchesPreview, language, markInvoicePaid, persistInvoiceId, resolveInvoicePricing]);

  const handleInvoice = () => {
    if (!email || !meetingId) {
      setErrorMsg(language ? "Meeting registration is incomplete" : "Хурлын бүртгэлийн мэдээлэл дутуу байна");
      return;
    }

    if (qpayInvoice?.qr_image) {
      return;
    }

    if (isBusy) {
      return;
    }

    localStorage.removeItem(legacyMeetingInvoiceStorageKey);
    const storedInvoiceId = localStorage.getItem(meetingInvoiceStorageKey);
    const reusableInvoiceId = normalizedInitialInvoiceId || storedInvoiceId;
    if (reusableInvoiceId && (localStorage.getItem("msidclienttoken") || normalizedAttendeeId)) {
      getExistingInvoice(reusableInvoiceId);
    }
    else {
      createNewInvoice();
    }
  }

  const createNewInvoice = ({ setLoading = true } = {}) => {
    if (setLoading) {
      setGettingInvoice(true);
    }
    setSuccessMsg("");
    setErrorMsg("");
    return serverInstance.post("/client/invoice/meeting", {
      email: email,
      resident: resident,
      meeting_id: meetingId,
      invoice_code: MEETING_QPAY_INVOICE_CODE,
      pricing_tier: pricingPreview?.tier,
      pricing_preview: pricingPreview,
      attendee_id: normalizedAttendeeId || undefined,
      coupon_code: normalizedCouponCode || undefined,
    }, getRequestConfig(localStorage.getItem("msidclienttoken")))
      .then((res) => {
        applyInvoiceResponse(res.data);
      })
      .catch((err) => {
        console.log(err);
        if (err?.response?.status === 401) {
          setErrorMsg("Та дахин нэвтрээд оролдоно уу!");
        }
        else {
          const message = err?.response?.data?.message || (typeof err?.response?.data === "string" ? err.response.data : "");
          setErrorMsg(message || "Төлбөрийн нэхэмжлэх үүсгэхэд алдаа гарлаа!");
        }
      })
      .finally(() => {
        if (setLoading) {
          setGettingInvoice(false);
        }
      });
  }

  const getExistingInvoice = (invoice_id) => {
    setGettingInvoice(true);
    setSuccessMsg("");
    const token = localStorage.getItem("msidclienttoken");
    const params = {
      invoiceId: invoice_id,
      meeting_id: meetingId,
      ...(normalizedAttendeeId ? { attendee_id: normalizedAttendeeId } : {}),
    };
    serverInstance.get("/client/invoice", getRequestConfig(token, params))
      .then((res) => {
        if (res.status === 200) {
          const qpayInvoice = typeof res.data.invoice.text_value === "string"
            ? JSON.parse(res.data.invoice.text_value)
            : res.data.invoice.text_value;
          const invoicePricing = normalizeInvoicePricing(res.data.invoice.pricing_snapshot || {
            amount: res.data.invoice.amount,
            currency: res.data.invoice.currency || "MNT",
            original_amount: res.data.invoice.original_amount,
            discount_amount: res.data.invoice.discount_amount,
            discount_code: res.data.invoice.discount_code,
            meeting_discount_amount: res.data.invoice.meeting_discount_amount,
            meeting_discount_code: res.data.invoice.meeting_discount_code,
            meeting_discount_label: res.data.invoice.meeting_discount_label,
            meeting_discount_ends_at: res.data.invoice.meeting_discount_ends_at,
            discount_ends_at: res.data.invoice.discount_ends_at,
            coupon_code: res.data.invoice.coupon_code,
            coupon_discount_amount: res.data.invoice.coupon_discount_amount,
            coupon_percent: res.data.invoice.coupon_percent,
            tier: res.data.invoice.pricing_tier,
          });
          const sameMeetingInvoice = res.data.invoice.product === "meeting"
            && String(res.data.invoice.product_id) === String(meetingId)
            && res.data.invoice.email === email
            && (!pricingPreview?.tier || invoicePricing.tier === pricingPreview.tier)
            && String(invoicePricing.coupon_code || "").trim().toUpperCase() === normalizedCouponCode
            && (normalizedCouponCode || !pricingPreview?.amount || Number(invoicePricing.amount) === Number(pricingPreview.amount));

          if (sameMeetingInvoice) {
            console.log("Same meeting invoice " + res.data.invoice.email, res.data.invoice.id)
            setInvoiceId(res.data.invoice.id);
            setQpayInvoice(qpayInvoice);
            setPricingInfo(invoicePricing);
            setErrorMsg("");
            setSuccessMsg("");
            persistInvoiceId(res.data.invoice.id);
          }
          else {
            persistInvoiceId("");
            return createNewInvoice({ setLoading: false });
          }
        }
        else {
          setSuccessMsg("");
          setErrorMsg("Төлбөрийн нэхэмжлэх үүсгэхэд алдаа гарлаа. Код: " + res.status);
        }
      })
      .catch((err) => {
        console.log(err);
        if (err?.response?.status === 401) {
          persistInvoiceId("");
          return createNewInvoice({ setLoading: false });
        }
        else if (err?.response?.status === 404 || err?.status === 404) {
          persistInvoiceId("");
          return createNewInvoice({ setLoading: false });
        }
        else {
          setErrorMsg("Төлбөрийн нэхэмжлэх үүсгэхэд алдаа гарлаа! " + (err?.response?.status || ""));
        }
      })
      .finally(() => setGettingInvoice(false));
  }

  const refreshInvoice = () => {
    if (!invoiceId || !meetingId || isBusy) {
      return;
    }

    setRefreshingInvoice(true);
    setSuccessMsg("");
    setErrorMsg("");
    serverInstance.post("/client/invoice/meeting/refresh", {
      meeting_id: meetingId,
      invoice_id: invoiceId,
      attendee_id: normalizedAttendeeId || undefined,
    }, getRequestConfig(localStorage.getItem("msidclienttoken")))
      .then((res) => {
        applyInvoiceResponse(res.data, { preserveExistingOnMismatch: true });
      })
      .catch((err) => {
        console.log(err);
        if (err?.response?.status === 401) {
          setErrorMsg(language ? "Please sign in again and try again" : "Та дахин нэвтрээд оролдоно уу!");
          return;
        }
        const message = err?.response?.data?.message || (typeof err?.response?.data === "string" ? err.response.data : "");
        setErrorMsg(message || (language ? "Could not refresh the payment invoice" : "Төлбөрийн нэхэмжлэх шинэчилж чадсангүй!"));
      })
      .finally(() => setRefreshingInvoice(false));
  }

  const checkInvoice = () => {
    if (!invoiceId || isBusy) {
      return;
    }
    setChecking(true)
    serverInstance.post("/client/invoice/check", {
      invoice_id: invoiceId
    }, getRequestConfig(localStorage.getItem("msidclienttoken")))
      .then((res) => {
        if (res.data.paid === true) {
          markInvoicePaid();
        }
        else {
          setSuccessMsg("");
          setErrorMsg("Төлбөр төлөгдөөгүй байна!");
          setPaid(false);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setChecking(false));
  }

  const earlyBirdEndDate = formatPricingDate(
    pricingSummary?.meeting_discount_ends_at || pricingSummary?.discount_ends_at || pricingPreview?.discount_ends_at,
    language
  );

  return (
    <div className='meeting-payment'>
      {errorMsg ? <div className="error-msg">{errorMsg}</div> : null}
      {successMsg ? <div className="success-msg">{successMsg}</div> : null}
      {!paid && pricingSummary ? (
        <div className="pricing-summary">
          <div className="pricing-line">
            <span>{language ? "Registration fee" : "Бүртгэлийн төлбөр"}</span>
            <strong>{formatAmount(pricingSummary.amount, pricingSummary.currency)}</strong>
          </div>
          {pricingSummary.tier ? (
            <div className="pricing-meta">
              {language ? "Type" : "Төрөл"}: {getTierLabel(pricingSummary.tier, language)}
            </div>
          ) : null}
          {Number(pricingSummary.discount_amount) > 0 ? (
            <>
              {Number(pricingSummary.meeting_discount_amount ?? pricingSummary.discount_amount) > 0 ? (
                <div className="pricing-discount">
                  <span>
                    {pricingSummary.meeting_discount_label || pricingSummary.discount_label || (language ? "Early bird discount" : "Early bird хөнгөлөлт")}: -{formatAmount(pricingSummary.meeting_discount_amount ?? pricingSummary.discount_amount, pricingSummary.currency)}
                  </span>
                  {earlyBirdEndDate ? (
                    <span className="pricing-discount-end-date">
                      {language ? "Ends" : "Дуусах"}: {earlyBirdEndDate}
                    </span>
                  ) : null}
                </div>
              ) : null}
              {Number(pricingSummary.coupon_discount_amount) > 0 ? (
                <div className="pricing-discount">
                  {language ? "Coupon" : "Купон"}{pricingSummary.coupon_code ? ` ${pricingSummary.coupon_code}` : ""}: -{formatAmount(pricingSummary.coupon_discount_amount, pricingSummary.currency)}
                </div>
              ) : null}
            </>
          ) : null}
          {Number(pricingSummary.original_amount) > Number(pricingSummary.amount) ? (
            <div className="pricing-meta">
              {language ? "Original fee" : "Үндсэн үнэ"}: {formatAmount(pricingSummary.original_amount, pricingSummary.currency)}
            </div>
          ) : null}
        </div>
      ) : null}

      {paid ? null :
        <>
          {qpayInvoice?.qr_image ?
            <img className="qr-code"
              src={`data:image/png;base64,${qpayInvoice?.qr_image}`}
              alt="QR Code"
              style={{ width: '300px', height: '300px' }}
            />
            :
            <div className="get-invoice">
              <button className="get" disabled={isBusy} onClick={handleInvoice}>
                {gettingInvoice ? (language ? "Please wait..." : "Түр хүлээнэ үү...") : "Qpay-р төлөх"}
              </button>
            </div>
          }

          {invoiceId ? <div className="invoice-actions">
            <div className="check-payment">
              <button className="check" disabled={isBusy} onClick={checkInvoice}>
                {checking ? (language ? "Checking..." : "Шалгаж байна...") : (language ? "Check payment" : "Төлбөр шалгах")}
              </button>
            </div>
            {qpayInvoice?.qr_image ? (
              <div className="refresh-payment">
                <button className="refresh" disabled={isBusy} onClick={refreshInvoice}>
                  {refreshingInvoice
                    ? (language ? "Refreshing..." : "Шинэчилж байна...")
                    : (language ? "Get new QPay QR" : "Шинэ QPay QR авах")}
                </button>
              </div>
            ) : null}
          </div> : null
          }

          {paid || !qpayInvoice?.qr_image ? null :
          <div className="mobile-only">
            <div className="title">Гар утасны аппаар төлөх:</div>
            <div className="payment-apps">
              {qpayInvoice?.urls.map((item, index) => (
                <div key={index} className="payment-app">
                  <a href={item.link} target="_blank" rel="noopener noreferrer">
                    <img className="logo" src={item.logo} alt={item.description || ""} />
                    <div className="name">{item.description}</div>
                  </a>
                </div>
              ))}
            </div>
          </div>}
        </>
      }
    </div>
  );
};

export default MeetingPayment;
